import React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { pdfGenerator } from './PDFGenerator'
import { Button, notification } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { buildCsvString, toSafeFilenamePart } from 'src/utils/csv'
import {
  parseCustomProductUnitPrice,
  resolveIngredientUnitPrice,
} from 'src/utils/priceFunctions'

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const normalizeValue = value =>
  String(value || '')
    .trim()
    .toLowerCase()

const getTodayDateForFilename = () => {
  const today = new Date()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  const year = today.getFullYear()
  return `${year}-${month}-${day}`
}

const getProductSnapshot = ingredient => ({
  ...(ingredient?.product || {}),
  // Keep ingredient-level values as fallbacks so export still works when product payload is partial.
  title: ingredient?.product?.title || ingredient?.title || '',
  sku: ingredient?.product?.sku || ingredient?.sku || '',
  size: ingredient?.product?.size || ingredient?.size || '',
  UoM: ingredient?.product?.UoM || ingredient?.UoM || '',
  supplierTitle:
    ingredient?.product?.supplierTitle || ingredient?.supplierTitle || '',
})

const getExportIngredientEntries = recipe => {
  const recipeEntries = (recipe?.ingredients || []).map(ingredient => ({
    ingredient,
    product: getProductSnapshot(ingredient),
    recipeId: recipe?._id || recipe?.id || recipe?.title || '',
    isCustom: false,
  }))

  const customEntries = (recipe?.customIngredients || []).map(ingredient => ({
    ingredient,
    product: getProductSnapshot(ingredient),
    recipeId: recipe?._id || recipe?.id || recipe?.title || '',
    isCustom: true,
  }))

  return [...recipeEntries, ...customEntries]
}

const buildIngredientKey = ({ productId, sku, title, size, uom }) => {
  const normalizedProductId = normalizeValue(productId)
  if (normalizedProductId) return `product:${normalizedProductId}`
  const normalizedSku = normalizeValue(sku)
  if (normalizedSku) return `sku:${normalizedSku}`
  return `fallback:${normalizeValue(title)}|${normalizeValue(
    size,
  )}|${normalizeValue(uom)}`
}

/**
 * Catalogue prices are already pounds (or pence, depending on the payload) and
 * custom products store them differently again, so defer to the shared helpers
 * rather than assuming a pence-to-pounds conversion.
 */
const getUnitPriceInPounds = entry => {
  const unitPrice = entry?.isCustom
    ? parseCustomProductUnitPrice(entry?.product?.price)
    : resolveIngredientUnitPrice({
        ingredient: entry?.ingredient,
        product: entry?.product,
      })

  return Number.isFinite(unitPrice) && unitPrice > 0 ? unitPrice.toFixed(2) : ''
}

const aggregateIngredients = recipes => {
  const ingredientsMap = new Map()

  ;(recipes || []).forEach(recipe => {
    const entries = getExportIngredientEntries(recipe)
    entries.forEach(entry => {
      const product = entry?.product || {}
      const supplier = entry?.isCustom
        ? 'Custom Ingredient'
        : product?.supplierTitle || 'Unknown Supplier'
      const sku = product?.sku || ''
      const title = product?.title || ''
      const size = product?.size || ''
      const uom = product?.UoM || ''
      const productId = product?.venueProductId || product?._id || ''
      const key = buildIngredientKey({ productId, sku, title, size, uom })

      if (!ingredientsMap.has(key)) {
        ingredientsMap.set(key, {
          supplier,
          sku,
          title,
          size,
          uom,
          unitPrice: getUnitPriceInPounds(entry),
          recipeIds: new Set(),
        })
      }

      const current = ingredientsMap.get(key)
      current.recipeIds.add(String(entry?.recipeId || ''))
      if (!current.unitPrice) {
        current.unitPrice = getUnitPriceInPounds(entry)
      }
      ingredientsMap.set(key, current)
    })
  })

  return Array.from(ingredientsMap.values())
    .map(item => ({
      supplier: item.supplier,
      sku: item.sku,
      title: item.title,
      size: item.size,
      uom: item.uom,
      unitPrice: item.unitPrice,
      usedInRecipeCount: item.recipeIds.size,
    }))
    .sort((a, b) => {
      const supplierOrder = a.supplier.localeCompare(b.supplier)
      if (supplierOrder !== 0) return supplierOrder
      return a.title.localeCompare(b.title)
    })
}

export const RecipeDownloadButton = ({
  recipe,
  recipes = [],
  allergenMetadata = {},
  venueProductsCatalog: venueProductsCatalogProp,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const venueProductsFromStore = useSelector(
    state => state.venueProducts?.products || [],
  )
  const venueProductsCatalog = Array.isArray(venueProductsCatalogProp)
    ? venueProductsCatalogProp
    : venueProductsFromStore

  const handleDownload = async () => {
    setIsLoading(true)
    try {
      await pdfGenerator.generateRecipePDF(
        recipe,
        recipes,
        allergenMetadata,
        venueProductsCatalog,
      )
    } catch (error) {
      console.error('Error generating PDF:', error)
      notification.error({
        message: 'Could not generate recipe PDF',
        description:
          'Please try again, or contact support if it keeps failing.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Button
        variant='primary'
        onClick={handleDownload}
        disabled={isLoading}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {isLoading ? <LoadingSpinner /> : <DownloadOutlined />}
        Download PDF
      </Button>
    </>
  )
}

export const AllRecipesDownloadButton = ({
  recipes,
  allergenMetadata = {},
  venueProductsCatalog: venueProductsCatalogProp,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const venueProductsFromStore = useSelector(
    state => state.venueProducts?.products || [],
  )
  const venueProductsCatalog = Array.isArray(venueProductsCatalogProp)
    ? venueProductsCatalogProp
    : venueProductsFromStore

  const handleDownload = async () => {
    setIsLoading(true)
    try {
      await pdfGenerator.generateAllRecipesPDF(
        recipes,
        allergenMetadata,
        venueProductsCatalog,
      )
    } catch (error) {
      console.error('Error generating PDF:', error)
      notification.error({
        message: 'Could not generate recipes PDF',
        description:
          'Please try again, or contact support if it keeps failing.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Button
        variant='primary'
        onClick={handleDownload}
        disabled={isLoading || recipes.length === 0}
        style={{ display: 'flex', alignItems: 'center', marginLeft: 5 }}
      >
        {isLoading ? <LoadingSpinner /> : <DownloadOutlined />}
        Download All Recipes as PDF
      </Button>
    </>
  )
}

export const DownloadIngredientsCsvButton = ({ recipes = [], venueTitle }) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = () => {
    setIsLoading(true)
    try {
      const aggregatedIngredients = aggregateIngredients(recipes)
      const rows = [
        [
          'Supplier',
          'SKU',
          'Product Name',
          'Size',
          'UoM',
          'Unit Price (£)',
          'Used In Recipe Count',
        ],
        ...aggregatedIngredients.map(ingredient => [
          ingredient.supplier,
          ingredient.sku,
          ingredient.title,
          ingredient.size,
          ingredient.uom,
          ingredient.unitPrice,
          ingredient.usedInRecipeCount,
        ]),
      ]

      const csvContent = buildCsvString(rows)
      const venueName = toSafeFilenamePart(venueTitle)
      const filename = `${venueName}_ingredients_${getTodayDateForFilename()}.csv`

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generating ingredients CSV:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Button
        variant='primary'
        onClick={handleDownload}
        disabled={isLoading || recipes.length === 0}
        style={{ display: 'flex', alignItems: 'center', marginLeft: 5 }}
      >
        {isLoading ? <LoadingSpinner /> : <DownloadOutlined />}
        Download Ingredients as CSV
      </Button>
    </>
  )
}
